import {useEffect} from 'react';

import {isMountedInShell} from '../shellRemote';
import {useSelector} from '../state/hooks';
import {useViewer} from '../state/viewer/hooks';
import {getViewerLoading} from '../state/viewer/selectors';
import {initDatadog} from './datadog/datadog';
import {startPerfObserver} from './datadog/perfObserver/performanceObserver';

/** How long to wait for the shell to enable Datadog before giving up. */
const DATADOG_FLAG_TIMEOUT_MS = 5000;
const DATADOG_FLAG_POLL_MS = 100;

/**
 * `initDatadog` returns early unless `window.DatadogEnabled` is set, and it is
 * the injector's `enableDatadog` that sets it — inside a consent-group
 * callback.
 *
 * Standalone, this app triggers those callbacks itself and the flag is set in
 * the same tick. Under the shell it is the shell that triggers them, off its
 * own viewer query, so the flag may not be set when this effect first runs.
 * Calling `initDatadog` once and losing the race would silently disable RUM for
 * the session, so poll briefly instead.
 *
 * Gives up rather than waiting forever: the flag legitimately never arrives
 * when the viewer is anonymous and declines the cookie category, or when
 * OneTrust fails to load.
 */
function initDatadogWhenEnabled(): () => void {
  if (window.DatadogEnabled) {
    initDatadog();
    return () => {};
  }

  const deadline = Date.now() + DATADOG_FLAG_TIMEOUT_MS;
  const poll = setInterval(() => {
    if (window.DatadogEnabled) {
      clearInterval(poll);
      initDatadog();
      return;
    }
    if (Date.now() >= deadline) {
      clearInterval(poll);
    }
  }, DATADOG_FLAG_POLL_MS);

  return () => clearInterval(poll);
}

export const useConsentAwareAnalyticsInjection = () => {
  const viewerIsLoading = useSelector(getViewerLoading);
  const viewer = useViewer();

  useEffect(() => {
    if (viewerIsLoading) {
      return;
    }

    let stopWaitingForDatadog = () => {};

    /*
      Both branches stay inside one try. Analytics setup must never take the app
      down with it: under the shell, `ShellErrorBoundary` in `shellMount.tsx`
      catches whatever this effect throws and renders the remote as `null`, so
      an `initDatadog` failure would blank the whole app inside Forge.
    */
    try {
      if (isMountedInShell()) {
        /*
          Consent belongs to the shell: it owns the session, and a remote cannot
          decide on another remote's behalf. Datadog and the perf observer stay
          this app's, gated by `window.DatadogEnabled` — which the shell sets
          only once that vendor is permitted.
        */
        stopWaitingForDatadog = initDatadogWhenEnabled();
      } else {
        /*
          Standalone. `consentAwareAnalyticsInjector.js` publishes the injector
          only on its consent path, so its absence means a private or
          telemetry-disabled deployment — and is what keeps Datadog and the perf
          observer off in a self-hosted install.
        */
        const injector = (window as any).WBAnalyticsInjector;
        if (!injector) {
          return;
        }
        injector.initializeTrackingScripts(!!viewer?.username);
        stopWaitingForDatadog = initDatadogWhenEnabled();
      }
    } catch (e) {
      return;
    }

    // Outside the try/catch so dev-only contract validation errors stay visible.
    startPerfObserver();

    return stopWaitingForDatadog;
  }, [viewer, viewerIsLoading]);
};
